import { List } from 'immutable';
import { fromSnowflakeProperty, createDefaultDimension, getDimensions, getMeasures, COLUMN_EXCLUDED_PREFIXES, setColumnTransform, getDefaultTransform } from 'reporting-snowflake/relational/utils/column-utils';
import { createDefaultColumnUsingExpression } from 'reporting-snowflake/relational/utils/visual-column-utils';
import { getVisualEncodedColumnIds } from 'reporting-snowflake/relational/utils/visual-utils';
import { getColumnValue, columnIsDuplicate } from './column-utils';
const getFilteredAndSortedColumns = (columns, visualEncodedColumnIds) => {
  return columns.filter(column => {
    const alias = column.get('alias');
    return !COLUMN_EXCLUDED_PREFIXES.some(prefix => alias.startsWith(prefix));
  }).sort((a, b) => {
    const aIndex = visualEncodedColumnIds.findKey(id => id === a.get('alias'));
    const bIndex = visualEncodedColumnIds.findKey(id => id === b.get('alias'));
    return aIndex - bIndex;
  }).toList();
};
const getDeduplicatedColumns = columns => {
  return columns.reduce((acc, col) => acc.some(existing => columnIsDuplicate(existing, col)) ? acc : acc.push(col), List());
};
export const getReportColumnsFromRelationalReport = report => {
  const visualEncodedColumnIds = getVisualEncodedColumnIds(report.visual).toList();
  const currentMeasureColumns = getFilteredAndSortedColumns(getMeasures(report.columns), visualEncodedColumnIds);
  const currentDimensionColumns = getFilteredAndSortedColumns(getDimensions(report.columns).toList(), visualEncodedColumnIds);
  const currentAllColumns = currentMeasureColumns.concat(currentDimensionColumns).toList();
  const allStagedColumns = report.stagedColumns;
  const allAvailableColumns = getDeduplicatedColumns(currentAllColumns.concat(allStagedColumns).toList());
  return {
    currentMeasureColumns,
    currentDimensionColumns,
    currentAllColumns,
    allAvailableColumns,
    allStagedColumns
  };
};
export const initialPropertiesByTable = metrics => {
  return metrics.reduce((acc, {
    field
  }) => {
    if (field.table && !acc[field.table]) {
      acc[field.table] = new Set();
    }
    if (field.table) {
      acc[field.table].add(field.name);
    }
    return acc;
  }, {});
};
export const filterListOfPropertiesByInitialProperties = (data, metrics) => {
  var _data$first;
  if ((_data$first = data.first()) !== null && _data$first !== void 0 && _data$first.objectTypeId) {
    const validPropertiesByTable = initialPropertiesByTable(metrics);
    return data.filter(item => {
      var _validPropertiesByTab;
      const field = item.value.field || item.value;
      return Boolean((field === null || field === void 0 ? void 0 : field.table) && ((_validPropertiesByTab = validPropertiesByTable[field.table]) === null || _validPropertiesByTab === void 0 ? void 0 : _validPropertiesByTab.has(field.name)));
    }).toArray();
  }
  const validProperties = metrics.reduce((acc, {
    field
  }) => {
    acc.add(field.name);
    return acc;
  }, new Set());
  return data.filter(item => {
    const alias = item.value.alias || item.value.name;
    return Boolean(alias && validProperties.has(alias));
  }).toArray();
};
export const getCurrentColumn = (relationalReport, metricAlias) => relationalReport.columns.find(column => column.alias === metricAlias);
export const getPropertyValue = currentColumn => currentColumn !== null && currentColumn !== void 0 && currentColumn.field.table ? `${currentColumn.field.table}.${currentColumn.field.name}` : `.${currentColumn.field.name}`;
export const removeDuplicateProperties = (topProperties, regularProperties) => {
  if (!topProperties.length || !regularProperties.length) {
    return regularProperties;
  }
  const topPropValues = new Set(topProperties.flatMap(group => group.options.map(option => option.value)));
  return regularProperties.map(group => Object.assign({}, group, {
    options: group.options.filter(option => !topPropValues.has(option.value))
  })).filter(group => group.options.length > 0);
};
const splitPropertyValue = propertyValue => {
  const [tableName, propertyName] = propertyValue.split('.');
  return [tableName || '', propertyName];
};
export const filterOptionGroupsByAvailableColumns = (optionGroups, validPropertiesByTable) => {
  return optionGroups.map(group => Object.assign({}, group, {
    options: group.options.filter(option => {
      var _validPropertiesByTab2;
      if (typeof option.value !== 'string') {
        return false;
      }
      const [tableName, propertyName] = splitPropertyValue(option.value);
      return Boolean((_validPropertiesByTab2 = validPropertiesByTable[tableName]) === null || _validPropertiesByTab2 === void 0 ? void 0 : _validPropertiesByTab2.has(propertyName));
    })
  })).filter(group => group.options.length > 0);
};
export const getIsFormulaField = (propertyValue, formulaFieldExpressions) => {
  const [__, propertyName] = splitPropertyValue(propertyValue);
  return formulaFieldExpressions.some(expression => expression.alias === propertyName);
};
export const removalIsDisabled = (propertyValue, dataColumns, formulaFieldExpressions) => {
  const [__, propertyName] = splitPropertyValue(propertyValue);
  return dataColumns.some(column => getColumnValue(column) === propertyValue) || formulaFieldExpressions.some(expression => expression.alias === propertyName);
};
export const propertyIsChecked = (propertyValue, dataColumns) => {
  return dataColumns.some(column => getColumnValue(column) === propertyValue);
};
export const getPropertyColumnByValue = (propertyValue, dataColumns) => dataColumns.find(column => getColumnValue(column) === propertyValue);
export const getPropertyDisplayText = (column, propertyGroupOptions) => {
  const columnValue = getColumnValue(column);
  const allOptions = propertyGroupOptions.flatMap(group => group.options);
  const matchingOption = allOptions.find(option => option.value === columnValue);
  return (matchingOption === null || matchingOption === void 0 ? void 0 : matchingOption.text) || columnValue;
};
export const getDefaultExpressionByPropertyValue = (propertyValue, defaultExpressions) => {
  var _defaultExpressions$f;
  const [__, targetAlias] = splitPropertyValue(propertyValue);
  return (_defaultExpressions$f = defaultExpressions.find(expression => expression.value.alias === targetAlias)) === null || _defaultExpressions$f === void 0 ? void 0 : _defaultExpressions$f.value;
};
const findDatasetFieldValue = (datasetFields, propertyName) => {
  var _datasetFields$datase, _datasetFields$derive, _datasetFields$proper;
  if (!datasetFields) {
    return undefined;
  }
  const measures = (_datasetFields$datase = datasetFields.datasetMeasures) === null || _datasetFields$datase === void 0 ? void 0 : _datasetFields$datase.measures;
  if ((measures === null || measures === void 0 ? void 0 : measures.size) > 0) {
    const found = measures.find(item => item.value.field.name === propertyName);
    if (found) {
      return found.value;
    }
  }
  const derivedFields = (_datasetFields$derive = datasetFields.derivedFields) === null || _datasetFields$derive === void 0 ? void 0 : _datasetFields$derive.fields;
  if ((derivedFields === null || derivedFields === void 0 ? void 0 : derivedFields.size) > 0) {
    const found = derivedFields.find(item => item.value.name === propertyName);
    if (found) {
      return createDefaultDimension(found.value);
    }
  }
  const propertyFields = (_datasetFields$proper = datasetFields.propertyFields) === null || _datasetFields$proper === void 0 ? void 0 : _datasetFields$proper.items;
  if ((propertyFields === null || propertyFields === void 0 ? void 0 : propertyFields.size) > 0) {
    const found = propertyFields.find(item => item.value.name === propertyName);
    if (found) {
      const baseColumn = createDefaultDimension(found.value);
      const defaultTransform = getDefaultTransform(baseColumn.field);
      if (defaultTransform) {
        return setColumnTransform(baseColumn, defaultTransform);
      }
      return baseColumn;
    }
  }
  return undefined;
};
export const getPropertyFromMeta = (propertyValue, tableAndPropertyMeta) => {
  const [tableName, propertyName] = splitPropertyValue(propertyValue);
  const regularProperty = tableAndPropertyMeta.getIn(['properties', tableName, propertyName]);
  if (regularProperty) {
    return regularProperty;
  }
  const datasetProperty = tableAndPropertyMeta.getIn(['datasetProperties', tableName, propertyName]);
  if (datasetProperty) {
    return datasetProperty;
  }
  return undefined;
};
export const getNewColumnFromPropertyValue = (propertyValue, tableAndPropertyMeta, defaultExpressions, defaultProperties, relationalReport, datasetFields = null) => {
  const [tableName, propertyName] = splitPropertyValue(propertyValue);
  const datasetField = findDatasetFieldValue(datasetFields, propertyName);
  if (datasetField) {
    return datasetField;
  }
  const defaultProperty = defaultProperties.find(property => property.value.field.table === tableName && property.value.field.name === propertyName);
  if (defaultProperty) {
    return defaultProperty.value;
  }
  const property = getPropertyFromMeta(propertyValue, tableAndPropertyMeta);
  if (property) {
    const baseColumn = createDefaultDimension(fromSnowflakeProperty(property, tableName));
    const defaultTransform = getDefaultTransform(baseColumn.field);
    if (defaultTransform) {
      return setColumnTransform(baseColumn, defaultTransform);
    }
    return baseColumn;
  }
  return createDefaultColumnUsingExpression(getDefaultExpressionByPropertyValue(propertyValue, defaultExpressions), undefined, 'TABLE', relationalReport.expressions).setIn(['field', 'table'], '');
};